import type { ReactNode } from 'react';
import styles from './DataTable.module.css';
import { EmptyState, ErrorState, LoadingState } from './States';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  caption: string;
  loading?: boolean;
  error?: unknown;
  errorTitle?: ReactNode;
  onRetry?: () => void;
  empty?: ReactNode;
  footer?: ReactNode;
}

/** Table shell in the Admin v2 style: uppercase muted heads, divided rows. */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  caption,
  loading,
  error,
  errorTitle,
  onRetry,
  empty,
  footer,
}: DataTableProps<T>) {
  let body: ReactNode;
  if (loading) body = <LoadingState />;
  else if (error)
    body = <ErrorState error={error} title={errorTitle} onRetry={onRetry} />;
  else if (!rows || rows.length === 0)
    body = empty ?? <EmptyState title="Nothing here yet" />;

  return (
    <div className={styles.wrap}>
      <table className={styles.table}>
        <caption className="visually-hidden">{caption}</caption>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                style={{ width: c.width, textAlign: c.align }}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        {!body && (
          <tbody>
            {rows!.map((row) => (
              <tr key={rowKey(row)}>
                {columns.map((c) => (
                  <td key={c.key} style={{ textAlign: c.align }}>
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        )}
      </table>
      {body}
      {footer && <div className={styles.footer}>{footer}</div>}
    </div>
  );
}
